import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Navbar from './Navbar';
import Footer from './Footer';
import './EventCalendarPage.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EventCalendarPage = ({ onNavigate }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [currentMonth, currentYear]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `http://localhost:3001/api/events/calendar?month=${currentMonth + 1}&year=${currentYear}`
      );
      const data = await response.json();
      setEvents(data.events || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const prevMonthDays = getDaysInMonth(
    currentMonth === 0 ? 11 : currentMonth - 1,
    currentMonth === 0 ? currentYear - 1 : currentYear
  );

  // Get events for a specific day
  const getEventsForDay = (day, type) => {
    if (type !== 'current') return [];
    
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => {
      const eventDate = new Date(event.start);
      const eventDateStr = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
      return eventDateStr === dateStr;
    });
  };

  const handleDayClick = (day, type) => {
    if (type !== 'current') return;
    const dayEvents = getEventsForDay(day, type);
    if (dayEvents.length > 0) {
      setSelectedDate({ day, events: dayEvents });
      setShowEventModal(true);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      meeting: '#3b82f6',
      festival: '#f59e0b',
      cultural: '#8b5cf6',
      spiritual: '#10b981',
      community: '#ec4899',
      other: '#6b7280'
    };
    return colors[category] || colors.other;
  };

  const formatTime = (dateString, timeString) => {
    if (timeString) return timeString;
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Build calendar grid
  const calendarDays = [];

  // Previous month trailing days
  for (let i = firstDay - 1; i >= 0; i--) {
    calendarDays.push({ day: prevMonthDays - i, type: 'prev' });
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    const isToday =
      i === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear();
    const dayEvents = getEventsForDay(i, 'current');
    calendarDays.push({ day: i, type: 'current', isToday, events: dayEvents });
  }

  // Next month leading days
  const remaining = 42 - calendarDays.length;
  for (let i = 1; i <= remaining; i++) {
    calendarDays.push({ day: i, type: 'next' });
  }

  return (
    <div className="ec-page">
      <Navbar onNavigate={onNavigate} light />

      {/* Page Title */}
      <div className="ec-title-wrapper">
        <h1 className="ec-page-title">Event Calendar</h1>
        <div className="ec-title-bar"></div>
      </div>

      {/* Calendar */}
      <div className="ec-calendar-container">
        <div className="ec-calendar">

          {/* Calendar Header */}
          <div className="ec-calendar-header">
            <button className="ec-nav-btn" onClick={prevMonth} aria-label="Previous month">
              <FiChevronLeft size={22} />
            </button>
            <h2 className="ec-month-year">
              {MONTH_NAMES[currentMonth]} {currentYear}
            </h2>
            <button className="ec-nav-btn" onClick={nextMonth} aria-label="Next month">
              <FiChevronRight size={22} />
            </button>
          </div>

          {/* Day names row */}
          <div className="ec-day-names">
            {DAY_NAMES.map((d) => (
              <div key={d} className="ec-day-name">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="ec-days-grid">
            {calendarDays.map((cell, idx) => (
              <div
                key={idx}
                className={
                  'ec-day-cell' +
                  (cell.type !== 'current' ? ' ec-day-muted' : '') +
                  (cell.isToday ? ' ec-day-today' : '') +
                  (cell.events && cell.events.length > 0 ? ' ec-day-has-events' : '')
                }
                onClick={() => handleDayClick(cell.day, cell.type)}
                style={{ cursor: cell.events && cell.events.length > 0 ? 'pointer' : 'default' }}
              >
                <span className="ec-day-number">{cell.day}</span>
                {cell.events && cell.events.length > 0 && (
                  <div className="ec-day-events">
                    {cell.events.slice(0, 2).map((event, i) => (
                      <div
                        key={i}
                        className="ec-event-dot"
                        style={{ backgroundColor: getCategoryColor(event.category) }}
                        title={event.title}
                      />
                    ))}
                    {cell.events.length > 2 && (
                      <span className="ec-more-events">+{cell.events.length - 2}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events List */}
        <div className="ec-upcoming-events">
          <h3>Upcoming Events</h3>
          {loading ? (
            <div className="ec-loading">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="ec-no-events">No events scheduled for this month</div>
          ) : (
            <div className="ec-events-list">
              {events
                .sort((a, b) => new Date(a.start) - new Date(b.start))
                .map((event, idx) => {
                  const eventDate = new Date(event.start);
                  return (
                    <div key={idx} className="ec-event-item" style={{ borderLeftColor: event.color }}>
                      <div className="ec-event-date">
                        <div className="ec-event-day">{eventDate.getDate()}</div>
                        <div className="ec-event-month">
                          {MONTH_NAMES[eventDate.getMonth()].substring(0, 3)}
                        </div>
                      </div>
                      <div className="ec-event-details">
                        <h4>{event.title}</h4>
                        {event.time && <p className="ec-event-time">🕐 {event.time}</p>}
                        {event.location && <p className="ec-event-location">📍 {event.location}</p>}
                        {event.description && <p className="ec-event-description">{event.description}</p>}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Event Modal */}
      {showEventModal && selectedDate && (
        <div className="ec-modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="ec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ec-modal-header">
              <h3>
                Events on {MONTH_NAMES[currentMonth]} {selectedDate.day}, {currentYear}
              </h3>
              <button className="ec-modal-close" onClick={() => setShowEventModal(false)}>
                ✕
              </button>
            </div>
            <div className="ec-modal-body">
              {selectedDate.events.map((event, idx) => (
                <div key={idx} className="ec-modal-event" style={{ borderLeftColor: event.color }}>
                  <h4>{event.title}</h4>
                  {event.time && <p className="ec-modal-time">🕐 {event.time}</p>}
                  {event.location && <p className="ec-modal-location">📍 {event.location}</p>}
                  {event.description && <p className="ec-modal-description">{event.description}</p>}
                  <span 
                    className="ec-modal-category"
                    style={{ backgroundColor: event.color }}
                  >
                    {event.category}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EventCalendarPage;
